import { CheckCircle } from '@mui/icons-material';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { environment } from '../../../config/environment';
import { useTranslation } from '../../../hooks/useTranslation';
import { useApiClient } from '../../../services/api';
import { useTransactionalEmailService } from '../../../services/transactionalEmailService';

interface PaymentDetails {
    payment_status: string;
    metadata: {
        packageId: string;
        taxYear: string;
        totalTransactions: string;
        expertReview?: string;
        dailySync?: string;
        portfolioInsights?: string;
        taxOptimization?: string;
        weDoEverything?: string;
        weDoEverythingCustomPricing?: string;
        individualSetup?: string;
    };
}

const PaymentSuccess = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
    const apiClient = useApiClient();
    const emailService = useTransactionalEmailService();

    const hasExpertService = (details: PaymentDetails) => {
        const metadata = details.metadata;
        return !!(
            metadata.expertReview === 'true' ||
            metadata.weDoEverything === 'true' ||
            metadata.individualSetup === 'true'
        );
    };

    const getFeatures = (details: PaymentDetails) => {
        const features = [];
        const metadata = details.metadata;

        // Base package (only if a package was purchased)
        if (metadata.packageId && metadata.taxYear) {
            // Use the same short description format as in OrderSummary and Settings
            features.push(
                t(`plansData.packages.${metadata.packageId}.short_description`, {
                    year: metadata.taxYear,
                    transactions: parseInt(metadata.totalTransactions) || 0,
                })
            );
        }

        // Add-ons - check for date strings (valid purchases) or 'true' values

        // Expert Review
        if (metadata.expertReview === 'true') {
            features.push(
                t('plansData.addons.expertReview.short_description', {
                    year: metadata.taxYear,
                })
            );
        }

        // Daily Sync - check for date string or 'true' value
        if (metadata.dailySync && metadata.dailySync !== 'false') {
            // Format the date for display
            const date =
                metadata.dailySync === 'true'
                    ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year from now as fallback
                    : new Date(metadata.dailySync);

            features.push(
                t('plansData.addons.dailySync.short_description', {
                    date: `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`,
                })
            );
        }

        // Portfolio Insights - check for date string or 'true' value
        if (metadata.portfolioInsights && metadata.portfolioInsights !== 'false') {
            // Format the date for display
            const date =
                metadata.portfolioInsights === 'true'
                    ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year from now as fallback
                    : new Date(metadata.portfolioInsights);

            features.push(
                t('plansData.addons.portfolioInsights.short_description', {
                    date: `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`,
                })
            );
        }

        // Tax Optimization - check for date string or 'true' value
        if (metadata.taxOptimization && metadata.taxOptimization !== 'false') {
            // Format the date for display
            const date =
                metadata.taxOptimization === 'true'
                    ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year from now as fallback
                    : new Date(metadata.taxOptimization);

            features.push(
                t('plansData.addons.taxOptimization.short_description', {
                    date: `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`,
                })
            );
        }

        if (metadata.weDoEverything && metadata.weDoEverything !== 'false') {
            features.push(t('plansData.addons.weDoEverything.short_description'));
        }

        if (metadata.individualSetup && metadata.individualSetup !== 'false') {
            features.push(t('plansData.addons.individualSetup.short_description'));
        }

        return features;
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        const individualSetup = searchParams.get('individualSetup');
        if (individualSetup) {
            setPaymentDetails({
                payment_status: 'succeeded',
                metadata: {
                    packageId: '',
                    taxYear: '',
                    totalTransactions: '',
                    individualSetup: 'true',
                },
            });
            setLoading(false);
            return;
        }

        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
            setError(t('plans.payment.success.error.invalidSession'));
            setLoading(false);
            return;
        }

        const verifyPayment = async () => {
            try {
                const response = await apiClient.get(
                    `${environment.expressApiUrl}/api/payment/verify-session/${sessionId}`
                );
                setPaymentDetails(response.data);

                const features = getFeatures(response.data).join(', ');
                await emailService.sendPurchaseSucceedEmail(features);
            } catch (err) {
                setError(t('plans.payment.success.error.verificationFailed'));
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [location, apiClient]);

    const renderFeatures = () => {
        if (!paymentDetails?.metadata) return null;
        const features = getFeatures(paymentDetails);

        // Log the features and metadata for debugging
        console.log('[PaymentSuccess] Metadata:', paymentDetails.metadata);
        console.log('[PaymentSuccess] Features:', features);

        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    py: 2,
                    px: 3,
                }}
            >
                {features.map((feature, index) => (
                    <Typography
                        key={index}
                        variant="body1"
                        sx={{
                            py: 1,
                            fontWeight: 500,
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        {feature}
                    </Typography>
                ))}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Paper
                sx={{
                    maxWidth: 600,
                    mx: 'auto',
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Typography variant="h5" color="error" gutterBottom>
                    {t('plans.payment.success.error.title')}
                </Typography>
                <Typography variant="body1" color="error.dark">
                    {error}
                </Typography>
                <Button
                    variant="contained"
                    sx={{
                        mt: 3,
                    }}
                    onClick={() => navigate('/plans')}
                >
                    {t('plans.payment.success.actions.returnToPlans')}
                </Button>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                maxWidth: 600,
                mx: 'auto',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: 3,
            }}
        >
            <CheckCircle
                sx={{
                    fontSize: 64,
                    color: 'success.main',
                    mb: 2,
                }}
            />

            <Typography variant="h4" gutterBottom>
                {paymentDetails?.metadata?.individualSetup === 'true'
                    ? t('plans.payment.success.titleReviewStarted')
                    : t('plans.payment.success.title')}
            </Typography>

            <Box
                sx={{
                    width: '100%',
                    maxWidth: 450,
                    mt: 3,
                    mb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 2,
                        textAlign: 'center',
                    }}
                >
                    {t('plans.payment.success.description')}
                </Typography>

                {renderFeatures()}

                {/* Expert Contact Message */}
                {paymentDetails && hasExpertService(paymentDetails) && (
                    <Box
                        sx={{
                            mt: 3,
                            px: 1.5,
                            py: 1.5,
                            backgroundColor: 'primary.main',
                            borderRadius: 2,
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            {t('plans.payment.success.expertContact')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    gap: 2,
                }}
            >
                {(() => {
                    const searchParams = new URLSearchParams(location.search);
                    const isOnboarding = searchParams.has('onboarding');
                    const hasWeDoEverything =
                        paymentDetails?.metadata?.weDoEverything && paymentDetails.metadata.weDoEverything !== 'false';

                    const isCustomPricing =
                        paymentDetails?.metadata?.weDoEverythingCustomPricing === 'true';

                    // If weDoEverything was purchased, show special button
                    if (hasWeDoEverything) {
                        // If it's custom pricing (individual payment link), use onboarding text and redirect to integrations
                        if (isCustomPricing) {
                            return (
                                <Button variant="contained" color="primary" onClick={() => navigate('/integrations')}>
                                    {t('plans.payment.success.buttonOnboarding')}
                                </Button>
                            );
                        }

                        // Otherwise, show explore app button
                        return (
                            <Button variant="contained" color="primary" onClick={() => navigate('/dashboard')}>
                                {t('plans.payment.success.buttonExploreApp')}
                            </Button>
                        );
                    }

                    // Handle individual setup separately
                    if (paymentDetails?.metadata?.individualSetup === 'true') {
                        return (
                            <Button variant="contained" color="primary" onClick={() => navigate('/dashboard')}>
                                {t('plans.payment.success.buttonExploreApp')}
                            </Button>
                        );
                    }

                    // Default behavior for other cases
                    return (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(isOnboarding ? '/integrations' : '/tax-reports')}
                        >
                            {isOnboarding
                                ? t('plans.payment.success.buttonOnboarding')
                                : t('plans.payment.success.button')}
                        </Button>
                    );
                })()}
            </Box>
        </Paper>
    );
};

export default PaymentSuccess;
